import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { Search, X } from 'lucide-react'
import { useMemo } from 'react'
import type { Mission } from '../api/models'

export type MissionTypeGroup = 'AUDIO' | 'DATA' | 'OTHERS'

export const MISSION_TYPE_GROUPS: Record<MissionTypeGroup, string[]> = {
  AUDIO: [
    'VOICE_CONVERSATION',
    'FREE_SPEECH',
    'PROFICIENCY_ASSESSMENT',
    'AUDIO_RECORDING',
    'AUDIO_ANNOTATION',
    'AUDIO_TRANSCRIPTION'
  ],
  DATA: ['FILE_UPLOAD'],
  OTHERS: ['PROFILE', 'STAKING', 'WEBSITE_LOGIN', 'EXTERNAL_ACTION']
}

export const MISSION_TYPE_GROUP_LABEL: Record<MissionTypeGroup, MessageDescriptor> = {
  AUDIO: msg`Audio missions`,
  DATA: msg`Data missions`,
  OTHERS: msg`Others`
}

const MISSION_TYPE_GROUP_ORDER: MissionTypeGroup[] = ['AUDIO', 'DATA', 'OTHERS']

const OTHERS_TYPES = new Set(MISSION_TYPE_GROUPS.OTHERS)
const AUDIO_TYPES = new Set(MISSION_TYPE_GROUPS.AUDIO)
const DATA_TYPES = new Set(MISSION_TYPE_GROUPS.DATA)

export const getMissionTypeGroup = (type: string): MissionTypeGroup => {
  if (AUDIO_TYPES.has(type)) return 'AUDIO'
  if (DATA_TYPES.has(type)) return 'DATA'
  if (OTHERS_TYPES.has(type)) return 'OTHERS'
  return 'OTHERS'
}

export type MissionFiltersValue = {
  group: MissionTypeGroup | null
  search: string
}

export const MISSION_FILTERS_DEFAULT: MissionFiltersValue = { group: null, search: '' }

export const applyMissionFilters = (missions: Mission[], filters: MissionFiltersValue): Mission[] => {
  const q = filters.search.trim().toLowerCase()
  if (!q && !filters.group) return missions
  return missions.filter((m) => {
    if (filters.group && getMissionTypeGroup(m.type) !== filters.group) return false
    if (q && !m.title.toLowerCase().startsWith(q)) return false
    return true
  })
}

type MissionFiltersProps = {
  missions: readonly Mission[]
  value: MissionFiltersValue
  onChange: (value: MissionFiltersValue) => void
  className?: string
}

export const MissionFilters = ({ missions, value, onChange, className }: MissionFiltersProps) => {
  const { t } = useLingui()
  const availableGroups = useMemo(() => {
    const present = new Set<MissionTypeGroup>()
    missions.forEach((m) => present.add(getMissionTypeGroup(m.type)))
    return MISSION_TYPE_GROUP_ORDER.filter((g) => present.has(g))
  }, [missions])

  const selectGroup = (group: MissionTypeGroup | null) => {
    onChange({ ...value, group })
  }

  return (
    <div className={clsx('flex flex-col gap-4', className)}>
      <div className="relative w-full max-w-[360px]">
        <Search className="text-gray-80 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <input
          type="text"
          value={value.search}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
          placeholder={t`Search missions...`}
          className={clsx(
            'backgroundColorBlackToWhite text-body-2 h-10 w-full rounded-xl border border-solid border-gray-100 pr-9 pl-9 outline-none',
            'focus:border-gray-80 transition-colors'
          )}
        />
        {value.search && (
          <button
            type="button"
            aria-label={t`Clear search`}
            onClick={() => onChange({ ...value, search: '' })}
            className="text-gray-80 hover:text-gray-70 absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {availableGroups.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => selectGroup(null)}
            className={clsx(
              'text-body-2 h-8 cursor-pointer rounded-lg border border-solid px-3 whitespace-nowrap transition-colors',
              value.group === null
                ? 'text-orange border-orange/40 bg-orange/10'
                : 'text-gray-80 hover:border-gray-80 border-gray-100'
            )}
          >
            <Trans>All</Trans>
          </button>
          {availableGroups.map((group) => {
            const isActive = value.group === group
            return (
              <button
                type="button"
                key={group}
                onClick={() => selectGroup(isActive ? null : group)}
                className={clsx(
                  'text-body-2 h-8 cursor-pointer rounded-lg border border-solid px-3 whitespace-nowrap transition-colors',
                  isActive
                    ? 'text-orange border-orange/40 bg-orange/10'
                    : 'text-gray-80 hover:border-gray-80 border-gray-100'
                )}
              >
                {t(MISSION_TYPE_GROUP_LABEL[group])}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
